<!-- ============================================================
  ClassDisableManagement.vue — close/reopen a specific group class on a
  specific date (tclassdisable). Manager + admin only.

  Flow: pick a date → the class picker lists only the classes that run on
  that weekday (tclassinfo.classday === weekday of the date, within its
  start/end range) → pick one → save. A closure makes /getClassTime report
  the slot as full (available 0) AND makes every booking write path reject it.

  Follows the standard admin patterns: <id-data-grid> + <id-modal> +
  <id-select> / <id-date> (all globally registered).
============================================================ -->
<template>
  <div>
    <div class="pg-head" v-if="!embedded">
      <div class="pg-ico"><span class="mdi mdi-calendar-lock"></span></div>
      <div>
        <div class="id-h1">{{ $t('classDisable.title') }}</div>
        <div class="pg-sub">{{ $t('classDisable.subtitle') }}</div>
      </div>
    </div>

    <id-data-grid
      :columns="headers"
      :rows="disableList"
      :search-keys="['coursename', 'classtime', 'description']"
      :search-placeholder="$t('btn.search')"
      :add-label="$t('classDisable.newClosure')"
      :loading="loadingList"
      @add="showAdd">
      <template #cell-classdate="{ row }">{{ format_date(row.classdate) }}</template>
      <template #cell-description="{ row }">{{ row.description || '-' }}</template>
      <template #cell-actions="{ row }">
        <span class="mdi mdi-calendar-check" v-hint="$t('classDisable.reopened')"
          style="color:var(--c-error);cursor:pointer;font-size:20px;padding:0 5px" @click="deleteItem(row)"></span>
      </template>
    </id-data-grid>

    <!-- per-date seat limits (tclasslimit): same class + date picker, but caps seats instead of closing -->
    <div class="modal-sec" style="margin-top:22px"><span class="mdi mdi-account-multiple-check"></span> {{ $t('classLimit.listTitle') }}</div>
    <id-data-grid
      :columns="limitHeaders"
      :rows="limitList"
      :search-keys="['coursename', 'classtime', 'description']"
      :search-placeholder="$t('btn.search')"
      :add-label="$t('classLimit.newLimit')"
      :loading="loadingList"
      @add="showAddLimit">
      <template #cell-classdate="{ row }">{{ format_date(row.classdate) }}</template>
      <template #cell-booked="{ row }">{{ row.booked }}<span v-if="Number(row.booked) > Number(row.maxperson)" style="color:var(--c-error)"> ⚠</span></template>
      <template #cell-description="{ row }">{{ row.description || '-' }}</template>
      <template #cell-actions="{ row }">
        <span class="mdi mdi-pencil" v-hint="$t('classLimit.edit')" :aria-label="$t('classLimit.edit')"
          style="cursor:pointer;font-size:20px;padding:0 5px" @click="editLimit(row)"></span>
        <span class="mdi mdi-delete" v-hint="$t('classLimit.remove')" :aria-label="$t('classLimit.remove')"
          style="color:var(--c-error);cursor:pointer;font-size:20px;padding:0 5px" @click="deleteLimit(row)"></span>
      </template>
    </id-data-grid>

    <!-- add closure / add seat-limit dialog (limitMode switches the extra field) -->
    <id-modal v-model="dialog" size="md" :icon="limitMode ? 'mdi-account-multiple-check' : 'mdi-calendar-lock'"
      :title="limitMode ? $t('classLimit.newLimit') : $t('classDisable.newClosure')" persistent>
      <div class="modal-sec"><span class="mdi mdi-calendar-remove"></span> {{ limitMode ? $t('classLimit.info') : $t('classDisable.closureInfo') }}</div>
      <div class="form-grid">
        <div class="field full"><label>{{ limitMode ? $t('classLimit.colDate') : $t('classDisable.dateLabel') }} <span class="req">*</span></label>
          <id-date v-model="editedItem.classdate" :placeholder="$t('common.selectDate')" :disabled="limitEditing" :holidays="holidays"
            @update:model-value="onDateChange"></id-date></div>

        <div class="field full"><label>{{ limitMode ? $t('classLimit.pickClass') : $t('classDisable.pickClass') }} <span class="req">*</span></label>
          <id-select v-model="editedItem.classid" searchable :disabled="limitEditing"
            :options="classOptions"
            :placeholder="classPickerPlaceholder"></id-select>
          <small v-if="editedItem.classdate && classOptions.length === 0" style="color:var(--c-error)">
            {{ isHolidayDate ? $t('classLimit.holidayDate') : $t('classDisable.noClassOnDate') }}</small></div>

        <div class="field full" v-if="limitMode"><label>{{ $t('classLimit.maxLabel') }} <span class="req">*</span></label>
          <input class="id-input" type="number" min="1" max="999" step="1" v-model="editedItem.maxperson" autocomplete="off">
          <small v-if="pickedClass">{{ $t('classLimit.usualHint', { n: pickedClass.maxperson }) }}</small></div>

        <div class="field full"><label>{{ $t('table.description') }}</label>
          <input class="id-input" v-model="editedItem.description" :placeholder="limitMode ? $t('classLimit.reason') : $t('classDisable.reason')"></div>
      </div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="close">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" :disabled="!editedItem.classdate || !editedItem.classid || (limitMode && !limitValid)" @click="save">
          <span class="mdi mdi-content-save"></span> {{ $t('btn.save') }}</button>
      </template>
    </id-modal>

    <!-- reopen (delete) confirm dialog -->
    <id-modal v-model="dialogDelete" size="sm" icon="mdi-calendar-check" :title="$t('common.confirmDeleteTitle')" persistent>
      <p style="margin:0">{{ $t('classDisable.confirmDelete', { class: closureLabel(editedItem), date: format_date(editedItem.classdate) }) }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="closeDelete">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="confirmDelete">
          <span class="mdi mdi-calendar-check"></span> {{ $t('btn.ok') }}</button>
      </template>
    </id-modal>

    <!-- remove seat limit confirm -->
    <id-modal v-model="dialogDeleteLimit" size="sm" icon="mdi-delete" :title="$t('common.confirmDeleteTitle')" persistent>
      <p style="margin:0">{{ $t('classLimit.confirmRemove', { class: closureLabel(editedItem), date: format_date(editedItem.classdate) }) }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="closeDeleteLimit">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="confirmDeleteLimit">
          <span class="mdi mdi-delete"></span> {{ $t('btn.ok') }}</button>
      </template>
    </id-modal>

    <!-- booking-impact warning: backend found existing bookings for this class on the
         chosen date (requireConfirm) — show the list, re-submit with confirmed:true -->
    <id-modal v-model="dialogBookingWarn" size="md" icon="mdi-alert" :title="$t('closures.bookingWarnTitle')" persistent>
      <p style="margin:0 0 10px">{{ $t('closures.bookingWarnLead', { date: format_date(editedItem.classdate), n: affected.total }) }}</p>
      <div v-if="affected.group.length" class="modal-sec"><span class="mdi mdi-account-group"></span> {{ $t('closures.bookingWarnGroup', { n: affected.group.length }) }}</div>
      <ul v-if="affected.group.length" class="warn-list">
        <li v-for="(g, i) in affected.group" :key="'g' + i">{{ g.classtime }} {{ g.coursename }} — {{ g.nickname }}</li>
      </ul>
      <p style="margin:10px 0 0;color:var(--c-error)">{{ $t('closures.bookingWarnNote') }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="closeBookingWarn">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="confirmSaveAnyway">
          <span class="mdi mdi-alert"></span> {{ $t('closures.bookingWarnConfirm') }}</button>
      </template>
    </id-modal>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import moment from 'moment'

// Locale-independent (0 = Sunday). Matches tclassinfo.classday, which the booking
// flow derives via toLocaleDateString('en-US', { weekday: 'long' }).
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export default {
  props: {
    // when hosted inside the merged ClosureManagement page, hide this component's own pg-head
    embedded: { type: Boolean, default: false },
  },
  data: () => ({
    dialog: false,
    dialogDelete: false,
    dialogBookingWarn: false,
    dialogDeleteLimit: false,
    limitMode: false,
    limitEditing: false, // editing an existing limit row: date + class are its key, so they are locked
    loadingList: false,
    disableList: [],
    limitList: [],
    holidays: [], // [{ holidaydate, description }] — a holiday closes every class, so no class is offered on it
    allClasses: [],
    // bookings the backend found for the chosen class+date (requireConfirm flow)
    affected: { group: [], private: [], total: 0 },
    editedItem: { classdate: null, classid: null, courseid: null, description: '', maxperson: '' },
    defaultItem: { classdate: null, classid: null, courseid: null, description: '', maxperson: '' },
  }),

  computed: {
    ...mapGetters({ token: 'getToken' }),
    headers() {
      return [
        { title: this.$t('classDisable.dateLabel'), key: 'classdate', sortable: true },
        { title: this.$t('table.courseName'), key: 'coursename', align: 'start' },
        { title: this.$t('table.classDay'), key: 'classday' },
        { title: this.$t('table.classTime'), key: 'classtime' },
        { title: this.$t('table.description'), key: 'description', sortable: false, align: 'start' },
        { title: this.$t('table.delete'), key: 'actions', sortable: false, align: 'center' },
      ]
    },
    limitHeaders() {
      return [
        { title: this.$t('classLimit.colDate'), key: 'classdate', sortable: true },
        { title: this.$t('table.courseName'), key: 'coursename', align: 'start' },
        { title: this.$t('table.classTime'), key: 'classtime' },
        { title: this.$t('classLimit.colMax'), key: 'maxperson' },
        { title: this.$t('classLimit.colUsual'), key: 'defaultmax' },
        { title: this.$t('classLimit.colBooked'), key: 'booked' },
        { title: this.$t('table.description'), key: 'description', sortable: false, align: 'start' },
        { title: this.$t('table.edit'), key: 'actions', sortable: false, align: 'center' },
      ]
    },
    isHolidayDate() {
      return !!this.editedItem.classdate && this.holidays.some(h => h.holidaydate === this.SQLDate(this.editedItem.classdate))
    },
    pickedClass() {
      return this.allClasses.find(c => String(c.classid) === String(this.editedItem.classid)) || null
    },
    limitValid() {
      const n = Number(this.editedItem.maxperson)
      return Number.isInteger(n) && n >= 1 && n <= 999
    },
    // classes that actually run on the chosen date (same weekday, within start/end,
    // enabled) and are not already closed on that date
    classOptions() {
      if (!this.editedItem.classdate || this.isHolidayDate) return []
      const date = this.SQLDate(this.editedItem.classdate)
      const weekday = WEEKDAYS[moment(this.editedItem.classdate, 'YYYY-MM-DD').day()]
      const alreadyClosed = new Set(
        this.disableList.filter(d => d.classdate === date).map(d => String(d.classid))
      )
      // only classes that are normally open for booking that day: class switched on, its course switched on
      // (both set in schedule management), and not closed for that date (tclassdisable). Editing a limit row keeps
      // its own class so the locked picker never goes blank.
      return this.allClasses
        .filter(c => String(c.classday) === weekday)
        .filter(c => (this.limitEditing && String(c.classid) === String(this.editedItem.classid))
          || (Number(c.enableflag) !== 0 && Number(c.course_enableflag ?? 1) !== 0 && !alreadyClosed.has(String(c.classid))))
        .filter(c => this.inRange(this.editedItem.classdate, c.startdate, c.enddate))
        .map(c => ({ value: c.classid, label: `${c.coursename} • ${c.classtime}` }))
    },
    classPickerPlaceholder() {
      return this.editedItem.classdate ? this.$t('classDisable.pickClassPlaceholder') : this.$t('classDisable.selectDateFirst')
    },
  },

  watch: {
    dialog(val) { val || this.close() },
    dialogDelete(val) { val || this.closeDelete() },
    dialogDeleteLimit(val) { val || this.closeDeleteLimit() },
  },

  async created() {
    await this.initialize()
  },

  methods: {
    async initialize() {
      // menu-entry load: grid skeleton for the real fetch only (no artificial $minLoad)
      this.loadingList = true
      const token = this.$store.getters.getToken
      await axios.get(this.baseURL + '/getAllClasses', { headers: { Authorization: `Bearer ${token}` } })
        .then(res => { if (res.data.success) this.allClasses = res.data.results })
        .catch(() => {})
      await axios.get(this.baseURL + '/getClassDisableList', { headers: { Authorization: `Bearer ${token}` } })
        .then(res => { if (res.data.success) this.disableList = res.data.results })
        .catch(() => {})
      await this.loadHolidays()
      await axios.get(this.baseURL + '/getClassLimitList', { headers: { Authorization: `Bearer ${token}` } })
        .then(res => { if (res.data.success) this.limitList = res.data.results })
        .catch(() => {})
      this.loadingList = false
    },

    // also re-read when a dialog opens: holidays are edited in the sibling tab of the same page
    async loadHolidays() {
      await axios.get(this.baseURL + '/collectHolidays', { headers: { Authorization: `Bearer ${this.$store.getters.getToken}` } })
        .then(res => { if (res.data.success) this.holidays = res.data.data })
        .catch(() => {})
    },

    inRange(date, start, end) {
      const d = moment(date, 'YYYY-MM-DD')
      if (start && d.isBefore(moment(start), 'day')) return false
      if (end && d.isAfter(moment(end), 'day')) return false
      return true
    },

    onDateChange() {
      // a different date may have a different set of classes → clear the picked class
      this.editedItem.classid = null
    },

    async showAdd() {
      this.limitMode = false
      this.limitEditing = false
      await this.loadHolidays()
      this.editedItem = Object.assign({}, this.defaultItem)
      this.dialog = true
    },
    async showAddLimit() {
      this.limitMode = true
      this.limitEditing = false
      await this.loadHolidays()
      this.editedItem = Object.assign({}, this.defaultItem)
      this.dialog = true
    },
    editLimit(row) {
      this.limitMode = true
      this.limitEditing = true
      this.editedItem = { classdate: row.classdate, classid: row.classid, courseid: row.courseid,
        description: row.description || '', maxperson: String(row.maxperson) }
      this.dialog = true
    },

    closureLabel(item) {
      if (!item) return ''
      return `${item.coursename || ''} ${item.classtime || ''}`.trim()
    },

    async save() {
      if (this.limitMode) return this.submitLimit()
      await this.submitClosure(false)
    },
    async submitLimit() {
      const cls = this.pickedClass
      if (!cls) return
      this.$emit('onLoading', true)
      const token = this.$store.getters.getToken
      await axios.post(this.baseURL + '/setClassLimit', {
        classid: cls.classid,
        classdate: this.SQLDate(this.editedItem.classdate),
        maxperson: Number(this.editedItem.maxperson),
        description: this.editedItem.description || '',
      }, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          if (res.data.success) {
            // existing bookings are never cancelled — say so when the new cap is already exceeded
            this.$emit('onInfoHandler', res.data.overLimit
              ? this.$t('classLimit.savedOver', { n: res.data.booked })
              : this.$t('classLimit.saved'))
            this.close()
            this.initialize()
          } else {
            this.$emit('onErrorHandler', res.data.message || this.$t('msg.addFail'))
          }
        })
        .catch(err => { this.$emit('onErrorHandler', err.message) })
      this.$emit('onLoading', false)
    },
    deleteLimit(item) {
      this.editedItem = Object.assign({}, item)
      this.dialogDeleteLimit = true
    },
    async confirmDeleteLimit() {
      this.$emit('onLoading', true)
      const token = this.$store.getters.getToken
      await axios.post(this.baseURL + '/deleteClassLimit', {
        classid: this.editedItem.classid,
        classdate: this.SQLDate(this.editedItem.classdate),
      }, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          if (res.data.success) {
            this.$emit('onInfoHandler', this.$t('classLimit.removed'))
            this.initialize()
          } else {
            this.$emit('onErrorHandler', res.data.message || this.$t('msg.updateFail'))
          }
        })
        .catch(err => { this.$emit('onErrorHandler', err.message) })
      this.closeDeleteLimit()
      this.$emit('onLoading', false)
    },
    closeDeleteLimit() {
      this.dialogDeleteLimit = false
      this.$nextTick(() => { this.editedItem = Object.assign({}, this.defaultItem) })
    },
    // re-submit with confirmed:true after the admin acknowledged the impacted bookings
    async confirmSaveAnyway() {
      await this.submitClosure(true)
    },
    async submitClosure(confirmed) {
      const cls = this.allClasses.find(c => String(c.classid) === String(this.editedItem.classid))
      if (!cls) return
      this.$emit('onLoading', true)
      const token = this.$store.getters.getToken
      await axios.post(this.baseURL + '/addClassDisable', {
        classid: cls.classid,
        courseid: cls.courseid,
        classdate: this.SQLDate(this.editedItem.classdate),
        description: this.editedItem.description || '',
        confirmed,
      }, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          if (res.data.success) {
            this.$emit('onInfoHandler', this.$t('classDisable.created'))
            this.closeBookingWarn()
            this.close()
            this.initialize()
          } else if (res.data.requireConfirm) {
            // class+date already has bookings — show the impact list and ask to confirm
            this.affected = res.data.affected || { group: [], private: [], total: 0 }
            this.dialogBookingWarn = true
          } else {
            this.$emit('onErrorHandler', res.data.message || this.$t('msg.addFail'))
          }
        })
        .catch(err => { this.$emit('onErrorHandler', err.message) })
      this.$emit('onLoading', false)
    },
    closeBookingWarn() {
      this.dialogBookingWarn = false
      this.affected = { group: [], private: [], total: 0 }
    },

    deleteItem(item) {
      this.editedItem = Object.assign({}, item)
      this.dialogDelete = true
    },

    async confirmDelete() {
      this.$emit('onLoading', true)
      const token = this.$store.getters.getToken
      await axios.post(this.baseURL + '/deleteClassDisable', {
        classid: this.editedItem.classid,
        courseid: this.editedItem.courseid,
        classdate: this.SQLDate(this.editedItem.classdate),
      }, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          if (res.data.success) {
            this.$emit('onInfoHandler', this.$t('classDisable.reopened'))
            this.initialize()
          } else {
            this.$emit('onErrorHandler', res.data.message || this.$t('msg.updateFail'))
          }
        })
        .catch(err => { this.$emit('onErrorHandler', err.message) })
      this.closeDelete()
      this.$emit('onLoading', false)
    },

    close() {
      this.dialog = false
      this.$nextTick(() => { this.editedItem = Object.assign({}, this.defaultItem) })
    },

    closeDelete() {
      this.dialogDelete = false
      this.$nextTick(() => { this.editedItem = Object.assign({}, this.defaultItem) })
    },

    SQLDate(date) { return moment(date).format('YYYY-MM-DD') },
    format_date(value) { return value ? moment(value).format('DD/MM/YYYY') : '' },
  },
}
</script>

<style scoped>
.warn-list {
  margin: 6px 0 0;
  padding-left: 20px;
  max-height: 180px;
  overflow-y: auto;
}
</style>
